import React, { useState, useCallback, useEffect } from 'react';
import { useAccount, useNetwork } from 'wagmi';
import { SimpleSEO } from '@/components/seo/SimpleSEO';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Trash2, Send, Plus, Download, AlertCircle, CheckCircle2, Info, ArrowLeftRight, ChevronDown, ChevronUp, ExternalLink, X } from 'lucide-react';
import { isAddress, formatUnits } from 'viem';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useMultisender } from '@/hooks/useMultisender';
import { LogoWithImage } from "@/components/icons/LogoWithImage";
import { initializeFirebase } from '@/lib/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useFirebaseFees } from '@/providers/FirebaseFeeProvider';

interface Recipient {
  address: string;
  amount: string;
  isValid: boolean;
}

export default function MultisenderPage() {
  const { address, isConnected } = useAccount();
  const { chain } = useNetwork();
  const { toast } = useToast();
  const { sendNative, sendToken, isSending: isMultisending, getTokenDecimals, getTokenBalance, getPlatformFee, txHash } = useMultisender();
  const { settings: feeSettings } = useFirebaseFees();

  // State
  const [tokenAddress, setTokenAddress] = useState('');
  const [recipients, setRecipients] = useState<Recipient[]>([{ address: '', amount: '', isValid: false }]);
  const [bulkInput, setBulkInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendMode, setSendMode] = useState<'native' | 'token'>('token');
  const [showPreview, setShowPreview] = useState(false);
  const [userTokens, setUserTokens] = useState<{ address: string; name: string; symbol: string }[]>([]);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [selectedUserToken, setSelectedUserToken] = useState('');
  const [tokenDecimals, setTokenDecimals] = useState<number | null>(null);
  const [tokenSymbol, setTokenSymbol] = useState<string | null>(null);
  const [tokenBalance, setTokenBalance] = useState<string | null>(null);
  const [isLoadingTokenMeta, setIsLoadingTokenMeta] = useState(false);
  const [isBulkImportExpanded, setIsBulkImportExpanded] = useState(false);
  const [successData, setSuccessData] = useState<{
    txHash: string;
    recipientCount: number;
    totalAmount: string;
    tokenSymbol: string;
    fee: string;
  } | null>(null);

  // Load tokens owned by the connected wallet for the current chain
  useEffect(() => {
    let isMounted = true;

    const loadUserTokens = async () => {
      if (!address || !chain?.id) {
        if (isMounted) {
          setUserTokens([]);
          setSelectedUserToken('');
        }
        return;
      }

      setIsLoadingTokens(true);

      try {
        const { db } = await initializeFirebase();
        const tokensRef = collection(db, 'tokens');
        const q = query(
          tokensRef,
          where('owner', '==', address),
          where('chainId', '==', chain.id)
        );

        const snapshot = await getDocs(q);
        const list = snapshot.docs
          .map((doc) => {
            const data = doc.data() as any;
            return {
              address: data.address || '',
              name: data.name || '',
              symbol: data.symbol || '',
            };
          })
          .filter((t) => t.address);

        if (isMounted) {
          setUserTokens(list);
          // Reset selection if current selection isn't in the new list
          if (!list.find((t) => t.address === selectedUserToken)) {
            setSelectedUserToken('');
          }
        }
      } catch (error) {
        console.error('Failed to load user tokens for multisender:', error);
        if (isMounted) {
          setUserTokens([]);
          setSelectedUserToken('');
        }
      } finally {
        if (isMounted) {
          setIsLoadingTokens(false);
        }
      }
    };

    loadUserTokens();

    return () => {
      isMounted = false;
    };
  }, [address, chain?.id]);

  // Load token metadata (decimals, balance, symbol) whenever tokenAddress changes
  useEffect(() => {
    let cancelled = false;

    const loadTokenMeta = async () => {
      if (!isConnected || !address || !chain?.id) {
        if (!cancelled) {
          setTokenDecimals(null);
          setTokenSymbol(null);
          setTokenBalance(null);
        }
        return;
      }

      if (!tokenAddress || !validateAddress(tokenAddress)) {
        if (!cancelled) {
          setTokenDecimals(null);
          setTokenSymbol(null);
          setTokenBalance(null);
        }
        return;
      }

      setIsLoadingTokenMeta(true);

      try {
        // Try to use saved metadata from Firestore list first
        const saved = userTokens.find(
          (t) => t.address.toLowerCase() === tokenAddress.toLowerCase()
        );
        if (saved) {
          setTokenSymbol(saved.symbol || saved.name || null);
        } else {
          setTokenSymbol(null);
        }

        const decimals = await getTokenDecimals(tokenAddress);
        const balanceBig = await getTokenBalance(tokenAddress, address);

        if (!cancelled) {
          setTokenDecimals(decimals);
          setTokenBalance(formatUnits(balanceBig, decimals));
        }
      } catch (error) {
        console.error('Failed to load token metadata for multisender:', error);
        if (!cancelled) {
          setTokenDecimals(null);
          setTokenBalance(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingTokenMeta(false);
        }
      }
    };

    loadTokenMeta();

    return () => {
      cancelled = true;
    };
  }, [tokenAddress, address, chain?.id, isConnected, userTokens, getTokenDecimals, getTokenBalance]);

  // Validation
  const validateAddress = (addr: string): boolean => {
    return isAddress(addr);
  };

  const validateAmount = (amount: string): boolean => {
    try {
      const parsed = parseFloat(amount);
      return parsed > 0 && !isNaN(parsed);
    } catch {
      return false;
    }
  };

  // Add recipient
  const addRecipient = () => {
    setRecipients([...recipients, { address: '', amount: '', isValid: false }]);
  };

  // Remove recipient
  const removeRecipient = (index: number) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  // Update recipient
  const updateRecipient = (index: number, field: 'address' | 'amount', value: string) => {
    const updated = [...recipients];
    updated[index][field] = value;
    updated[index].isValid = validateAddress(updated[index].address) && validateAmount(updated[index].amount);
    setRecipients(updated);
  };

  // Parse bulk input (CSV or line-by-line)
  const parseBulkInput = useCallback(() => {
    const lines = bulkInput.trim().split('\n');
    const parsed: Recipient[] = [];

    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // Support formats: "address,amount" or "address amount"
      const parts = trimmed.split(/[,\s]+/);
      if (parts.length >= 2) {
        const addr = parts[0].trim();
        const amt = parts[1].trim();
        parsed.push({
          address: addr,
          amount: amt,
          isValid: validateAddress(addr) && validateAmount(amt)
        });
      }
    });

    if (parsed.length > 0) {
      setRecipients(parsed);
      setBulkInput('');
      toast({
        title: "Success",
        description: `Imported ${parsed.length} recipients`,
      });
    } else {
      toast({
        title: "Error",
        description: "No valid recipients found in input",
        variant: "destructive",
      });
    }
  }, [bulkInput, toast]);

  // Import from CSV file
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setBulkInput(text);
    };
    reader.readAsText(file);
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = 'address,amount\n0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb,0.1\n0x123...,0.5';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'multisender-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate totals
  const calculateTotals = () => {
    const validRecipients = recipients.filter(r => r.isValid);
    const total = validRecipients.reduce((sum, r) => sum + parseFloat(r.amount), 0);
    return {
      count: validRecipients.length,
      total: total.toFixed(6),
      invalid: recipients.length - validRecipients.length
    };
  };

  const totals = calculateTotals();

  // State for contract fee
  const [contractFeePerRecipient, setContractFeePerRecipient] = useState<string>("0.001");

  // Fetch fee from contract when chain changes
  useEffect(() => {
    const fetchContractFee = async () => {
      if (!chain?.id) return;
      try {
        const feeForOne = await getPlatformFee(1, chain.id);
        const feeInEth = formatUnits(feeForOne, 18);
        setContractFeePerRecipient(feeInEth);
      } catch (error) {
        console.error('Failed to fetch contract fee:', error);
        setContractFeePerRecipient("0.001"); // Fallback
      }
    };
    fetchContractFee();
  }, [chain?.id, getPlatformFee]);

  // Get multisend fee from contract
  const getMultisendFee = () => {
    return contractFeePerRecipient;
  };

  const getCurrentNetworkSymbol = () => {
    return chain?.nativeCurrency?.symbol || "ETH";
  };

  // Calculate total fee cost
  const calculateTotalFee = () => {
    const validRecipients = recipients.filter(r => r.isValid);
    const feePerRecipient = parseFloat(getMultisendFee());
    const totalFee = feePerRecipient * validRecipients.length;
    return totalFee.toFixed(4);
  };

  // Update success data with txHash when it becomes available
  useEffect(() => {
    if (txHash && successData && !successData.txHash) {
      setSuccessData(prev => prev ? { ...prev, txHash } : null);
    }
  }, [txHash, successData]);

  // Handle send
  const handleSend = async () => {
    if (!isConnected) {
      toast({
        title: "Error",
        description: "Please connect your wallet",
        variant: "destructive",
      });
      return;
    }

    const validRecipients = recipients.filter(r => r.isValid);
    if (validRecipients.length === 0) {
      toast({
        title: "Error",
        description: "No valid recipients to send to",
        variant: "destructive",
      });
      return;
    }

    if (sendMode === 'token' && !validateAddress(tokenAddress)) {
      toast({
        title: "Error",
        description: "Please enter a valid token address",
        variant: "destructive",
      });
      return;
    }

    setShowPreview(true);
  };

  const confirmSend = async () => {
    setIsSending(true);
    try {
      const validRecipients = recipients.filter(r => r.isValid);
      const chainId = chain?.id || 97;
      
      let success = false;
      
      if (sendMode === 'native') {
        const result = await sendNative(validRecipients, chainId);
        success = result || false;
      } else {
        const result = await sendToken(tokenAddress, validRecipients, chainId);
        success = result || false;
      }
      
      if (success) {
        // Store success data (txHash will be added via useEffect)
        setSuccessData({
          txHash: '',
          recipientCount: validRecipients.length,
          totalAmount: totals.total,
          tokenSymbol: sendMode === 'native' ? (chain?.nativeCurrency?.symbol || 'BNB') : (tokenSymbol || 'Tokens'),
          fee: calculateTotalFee()
        });
        
        // Reset form
        setRecipients([{ address: '', amount: '', isValid: false }]);
        setTokenAddress('');
        setShowPreview(false);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send tokens",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const getExplorerUrl = (txHash: string) => {
    // BSC Networks
    if (chain?.id === 56) return `https://bscscan.com/tx/${txHash}`;
    if (chain?.id === 97) return `https://testnet.bscscan.com/tx/${txHash}`;
    
    // Ethereum Networks
    if (chain?.id === 1) return `https://etherscan.io/tx/${txHash}`;
    if (chain?.id === 11155111) return `https://sepolia.etherscan.io/tx/${txHash}`;
    
    // Avalanche Networks
    if (chain?.id === 43114) return `https://snowtrace.io/tx/${txHash}`;
    if (chain?.id === 43113) return `https://testnet.snowtrace.io/tx/${txHash}`;
    
    // Abstract Networks
    if (chain?.id === 2741) return `https://explorer.abs.xyz/tx/${txHash}`;
    if (chain?.id === 11124) return `https://explorer.testnet.abs.xyz/tx/${txHash}`;
    
    // Base Networks
    if (chain?.id === 8453) return `https://basescan.org/tx/${txHash}`;
    if (chain?.id === 84532) return `https://sepolia.basescan.org/tx/${txHash}`;
    
    // Optimism Networks
    if (chain?.id === 10) return `https://optimistic.etherscan.io/tx/${txHash}`;
    if (chain?.id === 11155420) return `https://sepolia-optimism.etherscan.io/tx/${txHash}`;
    
    // Arbitrum Networks
    if (chain?.id === 42161) return `https://arbiscan.io/tx/${txHash}`;
    if (chain?.id === 421614) return `https://sepolia.arbiscan.io/tx/${txHash}`;
    
    // Polygon Networks
    if (chain?.id === 137) return `https://polygonscan.com/tx/${txHash}`;
    if (chain?.id === 80002) return `https://amoy.polygonscan.com/tx/${txHash}`;
    
    // Default fallback
    return `https://etherscan.io/tx/${txHash}`;
  };

  return (
    <>
      <SimpleSEO 
        title="Multi Sender - Send Tokens to Multiple Addresses"
        description="Send tokens or native currency to multiple addresses in a single transaction. Save time and gas fees with our multi-sender tool."
      />

      <div>
        {/* Main Content */}
        <main className="container mx-auto px-4 py-8 max-w-6xl">
          <header className="text-center space-y-8 mb-8">
            <div className="flex items-center justify-center gap-4 sm:gap-6">
              <div className="matrix-logo-left shrink-0" style={{
                color: `var(--color-header-accent, #0A74FF)`
              }}>
                <LogoWithImage
                  className="w-8 h-8 sm:w-10 sm:h-10"
                  spin="counterclockwise"
                  IconComponent={ArrowLeftRight}
                />
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold py-4">
                <span
                  className="bg-clip-text text-transparent inline-block pb-2 leading-tight"
                  style={{
                    backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Multi Sender
                </span>
              </h1>
              <div className="matrix-logo-right shrink-0" style={{
                color: `var(--color-secondary, #10b981)`
              }}>
                <LogoWithImage
                  className="w-8 h-8 sm:w-10 sm:h-10"
                  spin="clockwise"
                  IconComponent={ArrowLeftRight}
                />
              </div>
            </div>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Send tokens or native currency to multiple addresses in a single transaction. Save time and reduce gas fees.
            </p>
          </header>

          {/* Success Card */}
          {successData && (
            <Card className="mb-6 border-green-500/50 bg-green-500/5">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                    <div>
                      <CardTitle className="text-green-600 dark:text-green-500">Transaction Successful!</CardTitle>
                      <CardDescription>Your tokens have been sent to {successData.recipientCount} recipients</CardDescription>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSuccessData(null)}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Recipients</p>
                    <p className="text-lg font-semibold">{successData.recipientCount}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total Sent</p>
                    <p className="text-lg font-semibold">{successData.totalAmount} {successData.tokenSymbol}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Platform Fee</p>
                    <p className="text-lg font-semibold text-yellow-600 dark:text-yellow-500">{successData.fee} {getCurrentNetworkSymbol()}</p>
                  </div>
                </div>
                
                {successData.txHash && (
                  <div className="pt-4 border-t space-y-3">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Transaction Hash</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted px-2 py-1 rounded flex-1 overflow-hidden text-ellipsis">
                          {successData.txHash}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(successData.txHash);
                            toast({
                              title: "Copied!",
                              description: "Transaction hash copied to clipboard",
                            });
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => window.open(getExplorerUrl(successData.txHash), '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Transaction on Block Explorer
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Send Mode Selection */}
              <Card>
                <CardHeader>
                  <CardTitle>Select Send Mode</CardTitle>
                  <CardDescription>Choose what you want to send</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      variant={sendMode === 'token' ? 'default' : 'outline'}
                      onClick={() => setSendMode('token')}
                      className="h-20"
                    >
                      <div className="text-center">
                        <div className="text-lg font-semibold">ERC20 Token</div>
                        <div className="text-xs opacity-70">Custom Token</div>
                      </div>
                    </Button>
                    <Button
                      variant={sendMode === 'native' ? 'default' : 'outline'}
                      onClick={() => setSendMode('native')}
                      className="h-20"
                    >
                      <div className="text-center">
                        <div className="text-lg font-semibold">Native Token</div>
                        <div className="text-xs opacity-70">{chain?.nativeCurrency?.symbol || 'ETH'}</div>
                      </div>
                    </Button>
                  </div>

                  {sendMode === 'token' && (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label>Token Contract Address</Label>
                        {isConnected && (
                          <span className="text-[11px] text-muted-foreground">
                            {isLoadingTokens
                              ? 'Loading your tokens…'
                              : userTokens.length > 0
                                ? `Your tokens on ${chain?.name ?? 'this network'}`
                                : 'No saved tokens for this wallet on this network'}
                          </span>
                        )}
                      </div>

                      {userTokens.length > 0 && (
                        <select
                          value={selectedUserToken}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedUserToken(value);
                            setTokenAddress(value);
                          }}
                          className="border border-input bg-background rounded-md px-2 py-1 text-sm w-full"
                          title="Select one of your created tokens to autofill the address"
                        >
                          <option value="">Select one of your tokens</option>
                          {userTokens.map((token) => (
                            <option key={token.address} value={token.address}>
                              {token.name || token.symbol || token.address.slice(0, 10)}
                              {token.symbol ? ` (${token.symbol})` : ''}
                            </option>
                          ))}
                        </select>
                      )}

                      <Input
                        placeholder="0x..."
                        value={tokenAddress}
                        onChange={(e) => {
                          setTokenAddress(e.target.value);
                          if (selectedUserToken && e.target.value !== selectedUserToken) {
                            setSelectedUserToken('');
                          }
                        }}
                        className={tokenAddress && !validateAddress(tokenAddress) ? 'border-red-500' : ''}
                      />
                      {tokenAddress && !validateAddress(tokenAddress) && (
                        <p className="text-xs text-destructive">Invalid token address</p>
                      )}
                      {sendMode === 'token' && tokenAddress && validateAddress(tokenAddress) && (
                        <p className="text-xs text-muted-foreground">
                          {isLoadingTokenMeta
                            ? 'Loading token info…'
                            : (
                              <>
                                {tokenSymbol && <span>{tokenSymbol} · </span>}
                                <span>Decimals: {tokenDecimals ?? 'Unknown'}</span>
                                {tokenBalance != null && (
                                  <span>{' · Your balance: '} {tokenBalance} {tokenSymbol || ''}</span>
                                )}
                              </>
                            )}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recipients List */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Recipients</CardTitle>
                      <CardDescription>Add addresses and amounts manually</CardDescription>
                    </div>
                    <Button onClick={addRecipient} size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {recipients.map((recipient, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <div className="flex-1 space-y-2">
                          <Input
                            placeholder="Recipient address (0x...)"
                            value={recipient.address}
                            onChange={(e) => updateRecipient(index, 'address', e.target.value)}
                            className={recipient.address && !validateAddress(recipient.address) ? 'border-red-500' : ''}
                          />
                          <Input
                            placeholder="Amount"
                            value={recipient.amount}
                            onChange={(e) => updateRecipient(index, 'amount', e.target.value)}
                            type="number"
                            step="0.000001"
                            className={recipient.amount && !validateAmount(recipient.amount) ? 'border-red-500' : ''}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRecipient(index)}
                          disabled={recipients.length === 1}
                          className="mt-1"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                        {recipient.isValid && (
                          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-500 mt-2" />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Bulk Import */}
              <Card>
                <CardHeader className="cursor-pointer" onClick={() => setIsBulkImportExpanded(!isBulkImportExpanded)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Bulk Import</CardTitle>
                      <CardDescription>Import multiple recipients at once</CardDescription>
                    </div>
                    <button
                      type="button"
                      className="p-2 hover:bg-muted rounded-md transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsBulkImportExpanded(!isBulkImportExpanded);
                      }}
                    >
                      {isBulkImportExpanded ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                  </div>
                </CardHeader>
                {isBulkImportExpanded && (
                  <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={downloadTemplate} className="flex-1">
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={() => document.getElementById('csv-upload')?.click()}>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload CSV/TXT
                    </Button>
                    <input
                      id="csv-upload"
                      type="file"
                      accept=".csv,.txt"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Paste Addresses & Amounts</Label>
                    <Textarea
                      placeholder="0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb,0.1&#10;0x123...,0.5&#10;&#10;Format: address,amount (one per line)"
                      value={bulkInput}
                      onChange={(e) => setBulkInput(e.target.value)}
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <Button onClick={parseBulkInput} disabled={!bulkInput.trim()} className="w-full">
                      Import Recipients
                    </Button>
                  </div>
                  </CardContent>
                )}
              </Card>
            </div>

            {/* Summary Sidebar */}
            <div className="space-y-6">
              {/* Summary Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Valid Recipients</span>
                      <span className="font-semibold text-foreground">{totals.count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Invalid Entries</span>
                      <span className="font-semibold text-destructive">{totals.invalid}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Platform Fee</span>
                      <span className="font-semibold text-yellow-600 dark:text-yellow-500">
                        {calculateTotalFee()} {getCurrentNetworkSymbol()}
                      </span>
                    </div>
                    <div className="border-t border-border pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-semibold text-foreground">Total Amount</span>
                        <span className="font-bold text-lg text-foreground">
                          {totals.total} {sendMode === 'native' ? chain?.nativeCurrency?.symbol : (tokenSymbol || 'Tokens')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleSend}
                    disabled={totals.count === 0 || !isConnected}
                    className="w-full"
                    size="lg"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send to {totals.count} Recipients
                  </Button>
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    How It Works
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <div>
                    <strong className="text-foreground">1. Choose Mode:</strong> Select native token or ERC20 token
                  </div>
                  <div>
                    <strong className="text-foreground">2. Add Recipients:</strong> Manually add or bulk import addresses
                  </div>
                  <div>
                    <strong className="text-foreground">3. Review:</strong> Check summary and confirm amounts
                  </div>
                  <div>
                    <strong className="text-foreground">4. Send:</strong> Execute in a single transaction
                  </div>
                  <Alert className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Make sure you have enough balance to cover the total amount plus gas fees
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Preview Modal */}
          {showPreview && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <Card className="max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                <CardHeader>
                  <CardTitle>Confirm Transaction</CardTitle>
                  <CardDescription>Review before sending</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Transaction Summary</AlertTitle>
                    <AlertDescription>
                      You are about to send {totals.total} {sendMode === 'native' ? chain?.nativeCurrency?.symbol : (tokenSymbol || 'tokens')} to {totals.count} recipients
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2 max-h-60 overflow-y-auto border border-border rounded-lg p-4 bg-card">
                    {recipients.filter(r => r.isValid).map((recipient, index) => (
                      <div key={index} className="flex justify-between text-sm py-2 border-b border-border last:border-0">
                        <span className="font-mono text-xs text-foreground">{recipient.address.slice(0, 10)}...{recipient.address.slice(-8)}</span>
                        <span className="font-semibold text-foreground">{recipient.amount}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setShowPreview(false)} className="flex-1" disabled={isSending || isMultisending}>
                      Cancel
                    </Button>
                    <Button onClick={confirmSend} disabled={isSending || isMultisending} className="flex-1">
                      {(isSending || isMultisending) ? 'Sending...' : `Send (Fee: ${calculateTotalFee()} ${getCurrentNetworkSymbol()})`}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
